import { useState } from 'react';
import { AlertCircle, ArrowLeft, Loader2 } from 'lucide-react';
import { useLoginMutation, useLoginVerifyMutation, useLazyGetProfileQuery } from '../../store/api/apiSlice';
import { setCredentials } from '../../store/slices/authSlice';
import { useAppDispatch } from '../../hooks/useAppStore';

interface InlineLoginProps {
    /** Called once tokens are stored and the profile is in the store. */
    onSuccess: () => void;
    onCancel: () => void;
}

function readError(err: unknown, fallback: string): string {
    const message = (err as { data?: { message?: unknown } })?.data?.message;
    if (Array.isArray(message)) return message.join(', ');
    if (typeof message === 'string') return message;
    return fallback;
}

/**
 * Signing in without leaving the checkout.
 *
 * Sending someone to /login mid-checkout means a full page navigation, which
 * throws away everything held in memory — including the documents they just
 * attached, which cannot be parked in a draft. Doing it here keeps the flow
 * mounted, so the moment they are authenticated the submission just proceeds
 * with exactly what they already entered.
 *
 * Same two-step OTP as the login page, because it is the same endpoints.
 */
export default function InlineLogin({ onSuccess, onCancel }: InlineLoginProps) {
    const dispatch = useAppDispatch();
    const [login, { isLoading: isRequestingOtp }] = useLoginMutation();
    const [loginVerify, { isLoading: isVerifying }] = useLoginVerifyMutation();
    const [getProfile] = useLazyGetProfileQuery();

    const [step, setStep] = useState<'email' | 'otp'>('email');
    const [email, setEmail] = useState('');
    const [otp, setOtp] = useState('');
    const [error, setError] = useState('');

    const requestOtp = async () => {
        setError('');
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
            setError('Enter a valid email address.');
            return;
        }

        const result = await login({ email: email.trim() });
        if ('data' in result && result.data) {
            setStep('otp');
        } else if ('error' in result) {
            setError(readError(result.error, 'Could not send the code. Please try again.'));
        }
    };

    const verifyOtp = async () => {
        setError('');
        if (otp.trim().length !== 6) {
            setError('Enter the 6-digit code.');
            return;
        }

        const result = await loginVerify({ email: email.trim(), otp: otp.trim() });
        if (!('data' in result) || !result.data) {
            setError(readError((result as { error?: unknown }).error, 'Invalid or expired code.'));
            return;
        }

        const data = result.data;
        localStorage.setItem('access_token', data.access_token);
        if (data.refresh_token) {
            localStorage.setItem('refresh_token', data.refresh_token);
        }

        // Prefer the full profile, but a failure here must not strand someone
        // who is already authenticated — the login response carries enough.
        try {
            const profile = await getProfile(undefined);
            dispatch(
                setCredentials({
                    user: 'data' in profile && profile.data ? profile.data : data.user,
                    token: data.access_token,
                }),
            );
        } catch {
            dispatch(setCredentials({ user: data.user, token: data.access_token }));
        }

        onSuccess();
    };

    const isBusy = isRequestingOtp || isVerifying;

    return (
        <div>
            <h2 className="text-lg font-bold text-gray-900">
                {step === 'email' ? 'Sign in to continue' : 'Enter your code'}
            </h2>
            <p className="mt-1.5 text-sm text-gray-600">
                {step === 'email'
                    ? 'We will send a 6-digit code to your email.'
                    : `We sent a 6-digit code to ${email}.`}
            </p>

            {step === 'email' ? (
                <input
                    type="email"
                    value={email}
                    autoFocus
                    onChange={(e) => setEmail(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && void requestOtp()}
                    placeholder="you@company.com"
                    className="mt-4 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm outline-none focus:border-[#FF6B35]"
                />
            ) : (
                <input
                    inputMode="numeric"
                    value={otp}
                    autoFocus
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    onKeyDown={(e) => e.key === 'Enter' && void verifyOtp()}
                    placeholder="123456"
                    className="mt-4 w-full rounded-xl border border-gray-200 px-4 py-3 text-center text-lg tracking-[0.4em] outline-none focus:border-[#FF6B35]"
                />
            )}

            {error && (
                <p className="mt-2 flex items-start gap-1.5 text-xs text-red-600">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    {error}
                </p>
            )}

            <button
                type="button"
                onClick={() => void (step === 'email' ? requestOtp() : verifyOtp())}
                disabled={isBusy}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl bg-[#FF6B35] py-3 text-sm font-semibold text-white transition-colors hover:bg-[#15171C] disabled:opacity-50"
            >
                {isBusy && <Loader2 className="h-4 w-4 animate-spin" />}
                {step === 'email' ? 'Send code' : 'Sign in and continue'}
            </button>

            <button
                type="button"
                onClick={() => (step === 'otp' ? setStep('email') : onCancel())}
                className="mt-4 flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-gray-800"
            >
                <ArrowLeft className="h-4 w-4" />
                {step === 'otp' ? 'Use a different email' : 'Back'}
            </button>
        </div>
    );
}
